
import axiosInstance from '../axiosInstance';
import { useEffect, useMemo, useState, useRef } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { FaDownload, FaExclamationTriangle, FaFilter } from 'react-icons/fa';
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { API_URL_All } from '../api';
import * as general from '../libs/general';
import { fetchReconcileNormalCustomerPermissions } from "../libs/customerCreatePermission";
import Paging from '../components/Paging';
import NormalReconciliationReview from '../components/orders/NormalReconciliationReview';
import ReconciliationPaymentConfirmModal, { buildPaymentSummary } from '../components/reconciliation/ReconciliationPaymentConfirmModal';
import AdLayout from '../components/AdLayout';
import DataTable from '../components/ui/DataTable';
import { CheckingTicket } from '../components/tickets/Tickets';

function NormalInvoices({ embedded = false }) {
  const [pageInvoiceData, setPageInvoiceData] = useState({ content: [] });
  const [isLoading, setIsLoading] = useState(false);

  const [checkingReviewData, setCheckingReviewData] = useState(null);
  const [paymentConfirm, setPaymentConfirm] = useState(null);

  const hasInvoiceRun = useRef(false);

  const [customers, setCustomers] = useState([]);
  const [customerCodes, setCustomerCodes] = useState("");
  const [params, setParams] = useState({});
  const [message, setMessage] = useState("");
  const [reconcilePerm, setReconcilePerm] = useState({
    loaded: false,
    canView: false,
    canCreate: false,
    canEdit: false,
    canDelete: false,
  });

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [draftCustomerCode, setDraftCustomerCode] = useState("");
  const [draftCustomerName, setDraftCustomerName] = useState("");
  const [draftCustomerEmail, setDraftCustomerEmail] = useState("");
  const [draftFileName, setDraftFileName] = useState("");
  const [draftDateFrom, setDraftDateFrom] = useState("");
  const [draftDateTo, setDraftDateTo] = useState("");
  const [draftCreatedBy, setDraftCreatedBy] = useState("");
  const [showCustomerCodeOptions, setShowCustomerCodeOptions] = useState(false);
  const [showCustomerNameOptions, setShowCustomerNameOptions] = useState(false);
  const [showCustomerEmailOptions, setShowCustomerEmailOptions] = useState(false);

  const isModalFilterActive = Boolean(
    params.customers ||
    params.customerNames ||
    params.customerEmail ||
    params.names ||
    params.dateFrom ||
    params.dateTo ||
    params.careNames
  );

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await axiosInstance.get(`/api/users/all`);
        const list = response.data || [];
        setCustomers(list);
        const codes = list.map(item => item.customerCode).filter(code => code);
        setCustomerCodes([...new Set(codes)].join(','));
      } catch (error) {
        console.error(error);
      }
    };

    fetchCustomers();
    searchInvoices();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await fetchReconcileNormalCustomerPermissions(axiosInstance);
      if (!cancelled) {
        setReconcilePerm({
          loaded: true,
          canView: Boolean(p?.canView),
          canCreate: Boolean(p?.canCreate),
          canEdit: Boolean(p?.canEdit),
          canDelete: Boolean(p?.canDelete),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const normalizeCustomerCode = (value) => (value || "").trim().toLowerCase();

  const uniqueCustomerCodes = useMemo(() => {
    return customers
      .map(c => c.customerCode)
      .filter(code => code && code.trim() !== "")
      .filter((code, index, self) => self.indexOf(code) === index);
  }, [customers]);

  const filteredCustomerCodes = useMemo(() => {
    const keyword = draftCustomerCode.trim().toLowerCase();
    if (!keyword) return uniqueCustomerCodes;
    const exactMatches = uniqueCustomerCodes.filter((code) => normalizeCustomerCode(code) === keyword);
    if (exactMatches.length > 0) return exactMatches;
    return uniqueCustomerCodes.filter((code) => code.toLowerCase().includes(keyword));
  }, [uniqueCustomerCodes, draftCustomerCode]);

  const uniqueCustomerNames = useMemo(() => {
    return customers
      .map(c => c.name)
      .filter(name => name && name.trim() !== "")
      .filter((name, index, self) => self.indexOf(name) === index);
  }, [customers]);

  const filteredCustomerNames = useMemo(() => {
    const keyword = draftCustomerName.trim().toLowerCase();
    if (!keyword) return uniqueCustomerNames;
    return uniqueCustomerNames.filter((name) => name.toLowerCase().includes(keyword));
  }, [uniqueCustomerNames, draftCustomerName]);

  const uniqueCustomerEmails = useMemo(() => {
    return customers
      .map(c => c.email)
      .filter(email => email && email.trim() !== "")
      .filter((email, index, self) => self.indexOf(email) === index);
  }, [customers]);

  const filteredCustomerEmails = useMemo(() => {
    const keyword = draftCustomerEmail.trim().toLowerCase();
    if (!keyword) return uniqueCustomerEmails;
    return uniqueCustomerEmails.filter((email) => email.toLowerCase().includes(keyword));
  }, [uniqueCustomerEmails, draftCustomerEmail]);

  const handleSearchChange = (e) => {
    var searchValue = e.target.value;
    searchInvoices({ page: 0, keyword: searchValue });
  };

  const handleRefreshPage = (e) => {
    searchInvoices();
  };

  const searchInvoices = async (filter) => {
    if (window.callingNormalInvoice) return;
    setIsLoading(true);
    window.callingNormalInvoice = true;
    var params = general.getSession('normal_invoice_filter');
    var formData = general.getFormData('#normal_invoice_filter');
    Object.assign(params, formData);

    if (!hasInvoiceRun.current) {
      hasInvoiceRun.current = true;
      var oldParams = general.getSession('normal_invoice_filter');
      Object.assign(params, oldParams);
    }

    if (filter) Object.assign(params, filter);
    general.setSession('normal_invoice_filter', params);
    setParams(params);
    window.initializeFilterValue(params);

    var filteredParams = Object.fromEntries(Object.entries(params).filter(([key, v]) => v !== undefined && v !== '' && key + v !== 'page0' && key + v !== 'size550' && key + v !== 'isDeletedfalse'));

    setMessage("");
    setPageInvoiceData({ content: [] });
    try {
      var response = await axiosInstance.get('/api/invoices/searchNormalInvoices', { params: filteredParams });
      setPageInvoiceData(response.data);
    } catch (error) {
      setMessage(error.message);
      console.error(error);
    } finally {
      window.callingNormalInvoice = false;
    }
    setIsLoading(false);
  }

  const toSemicolonList = (value) => (
    (value || "")
      .split(/[;,\n]/)
      .map((item) => item.trim())
      .filter(Boolean)
      .join(";")
  );

  const handleShowFilterModal = () => {
    setDraftCustomerCode((params.customers || "").replaceAll(";", ", "));
    setDraftCustomerName(params.customerNames || "");
    setDraftCustomerEmail(params.customerEmail || "");
    setDraftFileName(params.names || "");
    setDraftDateFrom(params.dateFrom || "");
    setDraftDateTo(params.dateTo || "");
    setDraftCreatedBy(params.careNames || "");
    setShowFilterModal(true);
  };

  const handleCloseFilterModal = () => setShowFilterModal(false);

  const handleApplyFilter = () => {
    const from = (draftDateFrom || "").trim();
    const to = (draftDateTo || "").trim();

    if (from && to && from > to) {
      toast.warning("Từ ngày không được lớn hơn Đến ngày.");
      return;
    }

    searchInvoices({
      page: 0,
      customers: toSemicolonList(draftCustomerCode),
      customerNames: (draftCustomerName || "").trim(),
      customerEmail: (draftCustomerEmail || "").trim(),
      names: (draftFileName || "").trim(),
      dateFrom: from,
      dateTo: to,
      careNames: (draftCreatedBy || "").trim(),
    });
    setShowFilterModal(false);
  };

  const handleResetFilter = () => {
    setDraftCustomerCode("");
    setDraftCustomerName("");
    setDraftCustomerEmail("");
    setDraftFileName("");
    setDraftDateFrom("");
    setDraftDateTo("");
    setDraftCreatedBy("");
    searchInvoices({
      page: 0,
      customers: "",
      customerNames: "",
      customerEmail: "",
      names: "",
      dateFrom: "",
      dateTo: "",
      careNames: "",
    });
    setShowFilterModal(false);
  };

  const handleChangeItemsPerPage = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    searchInvoices({ page: 0, size: newItemsPerPage });
  };

  const handlePageChange = (page) => {
    searchInvoices({ page: page - 1 });
  };

  const openInvoiceDetail = async (id) => {
    if (!reconcilePerm.loaded || !reconcilePerm.canView) {
      toast.warning("Bạn không có quyền xem chi tiết phiếu đối soát.");
      return;
    }
    setCheckingReviewData({ id: id });
  };

  const handlePaymentConfirm = (item) => {
    if (!reconcilePerm.loaded || !reconcilePerm.canEdit) {
      toast.warning("Bạn không có quyền cập nhật đối soát.");
      return;
    }
    setPaymentConfirm(item);
  };

  const handleExportInvoice = async (param) => {
    try {
      var requestApi = `${API_URL_All}/api/invoices/exportNormalInvoice?id=${param.id}`;
      var fileName = `Doi_soat_thuong_${param.id}.xlsx`;
      await general.downloadFile(requestApi, fileName);
    } catch (error) {
      toast.error(error.message);
      console.log(error);
    }
  };

  const content = (
    <>
      <div className="table-responsive web-table">
        {!embedded && (
          <div className="tab-container">
            <nav>
              <div className="nav nav-tabs" id="nav-tab" role="tablist">
                <button className="nav-link"><Link to="/quan-li-doi-soat">Đơn hàng thường: Chưa đối soát</Link></button>
                <button className="nav-link active">Đã đối soát</button>
              </div>
            </nav>
          </div>
        )}

        <div className="container-fluid pt-2">
          <div className="text-start mb-2 normal-form normal-invoice-filter-bar">
            <div className="row align-items-center g-2">
              <div className="col-12 col-md-7 col-lg-6">
                <form id="normal_invoice_filter">
                  <div className="row g-2 align-items-center normal-invoice-filter-actions">
                    <div className="col-12 col-sm-7 col-md-6">
                      <div className="row g-1 align-items-center">
                        <label className="form-label col-form-label col-3 col-md-4 mb-0">Tìm kiếm</label>
                        <div className="col-9 col-md-8">
                          <input className="form-control form-control-sm" type="text" name="keyword" placeholder="Nhập từ khóa..." autoComplete="off" value={params.keyword || ''} onChange={(e) => handleSearchChange(e)} />
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-sm-5 col-md-6 normal-invoice-filter-btns">
                      <button onClick={(event) => searchInvoices({ page: 0 })} type="button" className="btn btn-info btn-sm">
                        Tìm kiếm
                      </button>
                      <button
                        type="button"
                        className={`btn btn-info btn-sm ${isModalFilterActive ? "blinking-button" : ""}`}
                        title="Bộ lọc"
                        onClick={handleShowFilterModal}
                      >
                        <FaFilter /> Bộ lọc
                      </button>
                    </div>
                  </div>
                </form>
              </div>
              <div className="col-12 col-md-5 col-lg-6 normal-invoice-filter-des-wrap">
                <span className="filter-des hidden-close-i2"></span>
                {isModalFilterActive && (
                  <span className="normal-invoice-date-range-chip" title="Bộ lọc đang áp dụng">
                    {[
                      params.customers && `KH: ${params.customers}`,
                      params.customerNames && `Tên: ${params.customerNames}`,
                      params.customerEmail && `Email: ${params.customerEmail}`,
                      params.names && `File: ${params.names}`,
                      (params.dateFrom || params.dateTo) && `${params.dateFrom || "..."} → ${params.dateTo || "..."}`,
                      params.careNames && `Người tạo: ${params.careNames}`,
                    ].filter(Boolean).join(" | ")}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <section className="p-1">
          <DataTable maxHeight="calc(100vh - 220px)" className="table-normal" data-loading={isLoading}>
            <table className="table table-striped table-bordered border-normal">
              <thead className="table-primary">
                <tr>
                  <th width="30px" className="sticky text-center">STT</th>
                  <th className="text-center">
                    <span>Ngày đối soát</span>
                    <span className="filter-ib" data-form="normal_invoice_filter" data-filter="dates" data-source-url={API_URL_All + '/api/invoices/searchNormalInvoiceDates'} data-min="100px"><i className="filter-i"></i></span>
                  </th>
                  <th className="text-center">
                    <span>Mã KH</span>
                    <span className="filter-ib" data-form="normal_invoice_filter" data-filter="customers" data-source-url={API_URL_All + '/api/users/searchUserCodes'} data-min="100px"><i className="filter-i"></i></span>
                  </th>
                  <th className="text-start">
                    <span>Tên KH</span>
                    <span className="filter-ib" data-form="normal_invoice_filter" data-filter="customerNames" data-source-url={API_URL_All + '/api/invoices/searchNormalInvoiceCustomerNames'} data-min="100px"><i className="filter-i"></i></span>
                  </th>
                  <th className="text-start"><span>Nội dung</span></th>
                  <th className="text-center"><span>Số đơn</span></th>
                  <th className="text-center"><span data-note="">Tổng cước</span></th>
                  <th>
                    <span>Trạng thái</span>
                    <span className="filter-ib" data-form="normal_invoice_filter" data-filter="rateStatus" data-source-key="list_rateStatus" data-min="100px"><i className="filter-i"></i></span>
                  </th>
                  <th>
                    <span>Người xử lý</span>
                    <span className="filter-ib" data-form="normal_invoice_filter" data-filter="careNames" data-source-url={API_URL_All + '/api/invoices/searchNormalInvoiceCareNames'} data-min="100px"><i className="filter-i"></i></span>
                  </th>
                  <th><span>Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {pageInvoiceData.page && pageInvoiceData.page.content && pageInvoiceData.page.content.map((item, index) => (
                  <tr key={index}>
                    <td className="sticky text-center" title={`id: ${item.id}`}>{(pageInvoiceData.page.number * pageInvoiceData.page.size) + index + 1}</td>
                    <td className="text-center" title={general.formatTimeShort(item.createdAt)}>{general.formatDate(item.createdAt)}</td>
                    <td className="text-center">{item.customerCode}</td>
                    <td className="text-start">{item.customerName}</td>
                    <td className="text-start">{item.name}</td>
                    <td className="text-center">{item.orderCount}</td>
                    {item.changedOrderCount > 0 ? (
                      <td
                        className="text-center text-danger fw-bold"
                        title={`${item.changedOrderCount} đơn bị lệch so với lúc tạo phiếu. Cước theo đơn hiện tại: ${general.formatNumber(item.currentFee)}`}
                      >
                        <FaExclamationTriangle className="me-1" size={12} aria-hidden />
                        {general.formatNumber(item.fee)}
                      </td>
                    ) : (
                      <td className="text-center">{general.formatNumber(item.fee)}</td>
                    )}
                    <td className="text-start">
                      <CheckingTicket checkingId={item.checkingId} checkingName={item.checkingName} />
                      {item.checkingId === 14 && reconcilePerm.canEdit && (
                        <span>
                          <span className="btn-sm btn btn-primary ms-2" onClick={(e) => handlePaymentConfirm(item)}>Xác nhận thanh toán</span>
                        </span>
                      )}
                    </td>
                    <td className="text-start">{item.careName || "—"}</td>
                    <td className="text-center">
                      <span className="small-btn" title="Xuất file" onClick={(e) => { handleExportInvoice({ id: item.id }) }}><FaDownload /></span>
                      <span className="small-btn" onClick={(e) => { openInvoiceDetail(item.id) }}>xem</span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tbody>
                {message === '' && pageInvoiceData.page && pageInvoiceData.page.content.length === 0 && (
                  <tr>
                    <td colSpan="10" className="text-center">Không tìm thấy dữ liệu</td>
                  </tr>
                )}
                {message !== '' && (
                  <tr>
                    <td colSpan="10" className="text-center">{message}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </DataTable>
        </section>

        <div className="my-1 d-flex justify-content-end align-items-center">
          <Paging
            pageData={pageInvoiceData}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleChangeItemsPerPage}
          />
        </div>

        <input type="hidden" id="list_customers" value={customerCodes} />
        <input type="hidden" id="list_rateStatus" value="Vào phiếu,Đã đối soát" />

        {checkingReviewData && (
          <NormalReconciliationReview data={checkingReviewData} handleSearch={handleRefreshPage} canEdit={reconcilePerm.canEdit} />
        )}

        {paymentConfirm && (
          <ReconciliationPaymentConfirmModal
            show={!!paymentConfirm}
            onHide={() => setPaymentConfirm(null)}
            onSuccess={handleRefreshPage}
            completeApi="/api/invoices/completeNormalInvoice"
            payloadBase={{ id: paymentConfirm.id }}
            summary={buildPaymentSummary(paymentConfirm, "customer")}
            accountLabel="Tài khoản nhận tiền"
          />
        )}

      </div >

      <Modal
        show={showFilterModal}
        onHide={handleCloseFilterModal}
        centered
        size="xl"
        fullscreen="sm-down"
        className="fw-bold normal-invoice-filter-modal"
      >
        <Modal.Header closeButton className="p-2">
          <Modal.Title>Bộ lọc</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-3">
              <Col xs={12} md={6}>
                <Form.Label>Mã KH</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    value={draftCustomerCode}
                    onChange={(e) => {
                      setDraftCustomerCode(e.target.value);
                      setShowCustomerCodeOptions(true);
                    }}
                    onFocus={() => setShowCustomerCodeOptions(true)}
                    onBlur={() => setTimeout(() => setShowCustomerCodeOptions(false), 150)}
                    placeholder="Nhập mã khách hàng..."
                  />
                  {showCustomerCodeOptions && filteredCustomerCodes.length > 0 && (
                    <div
                      className="position-absolute w-100 bg-white border rounded shadow-sm zto-autocomplete-menu"
                    >
                      {filteredCustomerCodes.map((code, idx) => (
                        <div
                          key={idx}
                          className="px-2 py-1 fw-normal"
                          role="button"
                          onMouseDown={() => {
                            setDraftCustomerCode(code);
                            setShowCustomerCodeOptions(false);
                          }}
                        >
                          {code}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Col>

              <Col xs={12} md={6}>
                <Form.Label>Tên KH</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    value={draftCustomerName}
                    onChange={(e) => {
                      setDraftCustomerName(e.target.value);
                      setShowCustomerNameOptions(true);
                    }}
                    onFocus={() => setShowCustomerNameOptions(true)}
                    onBlur={() => setTimeout(() => setShowCustomerNameOptions(false), 150)}
                    placeholder="Nhập tên khách hàng..."
                  />
                  {showCustomerNameOptions && filteredCustomerNames.length > 0 && (
                    <div
                      className="position-absolute w-100 bg-white border rounded shadow-sm zto-autocomplete-menu"
                    >
                      {filteredCustomerNames.map((name, idx) => (
                        <div
                          key={idx}
                          className="px-2 py-1 fw-normal"
                          role="button"
                          onMouseDown={() => {
                            setDraftCustomerName(name);
                            setShowCustomerNameOptions(false);
                          }}
                        >
                          {name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Col>

              <Col xs={12} md={6}>
                <Form.Label>Email KH</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    value={draftCustomerEmail}
                    onChange={(e) => {
                      setDraftCustomerEmail(e.target.value);
                      setShowCustomerEmailOptions(true);
                    }}
                    onFocus={() => setShowCustomerEmailOptions(true)}
                    onBlur={() => setTimeout(() => setShowCustomerEmailOptions(false), 150)}
                    placeholder="Nhập email khách hàng..."
                  />
                  {showCustomerEmailOptions && filteredCustomerEmails.length > 0 && (
                    <div
                      className="position-absolute w-100 bg-white border rounded shadow-sm zto-autocomplete-menu"
                    >
                      {filteredCustomerEmails.map((email, idx) => (
                        <div
                          key={idx}
                          className="px-2 py-1 fw-normal"
                          role="button"
                          onMouseDown={() => {
                            setDraftCustomerEmail(email);
                            setShowCustomerEmailOptions(false);
                          }}
                        >
                          {email}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Col>

              <Col xs={12} md={6}>
                <Form.Label>Tên file</Form.Label>
                <Form.Control
                  type="text"
                  value={draftFileName}
                  onChange={(e) => setDraftFileName(e.target.value)}
                  placeholder="Nội dung / tên phiếu đối soát..."
                />
              </Col>

              <Col xs={12} md={6}>
                <Form.Label>Từ ngày</Form.Label>
                <Form.Control
                  type="date"
                  value={draftDateFrom}
                  onChange={(e) => setDraftDateFrom(e.target.value)}
                />
              </Col>

              <Col xs={12} md={6}>
                <Form.Label>Đến ngày</Form.Label>
                <Form.Control
                  type="date"
                  value={draftDateTo}
                  onChange={(e) => setDraftDateTo(e.target.value)}
                />
              </Col>

              <Col xs={12} md={6}>
                <Form.Label>Người tạo</Form.Label>
                <Form.Control
                  type="text"
                  value={draftCreatedBy}
                  onChange={(e) => setDraftCreatedBy(e.target.value)}
                  placeholder="Người xử lý phiếu đối soát..."
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="normal-invoice-filter-modal-footer">
          <Button variant="primary" className="btn-sm" onClick={handleApplyFilter}>Áp dụng</Button>
          <Button variant="danger" className="btn-sm" onClick={handleResetFilter}>Reset</Button>
        </Modal.Footer>
      </Modal>
    </>
  );

  if (embedded) return content;

  return (
    <div>
      <AdLayout>
        {content}
      </AdLayout>
    </div>
  );
}
export default NormalInvoices;
